// src/index.jsx — app entry point.
//
// Chooses which app to mount:
//   /beta, /beta/...      → src-beta/App.jsx at /beta  (only when REACT_APP_JOIN_ENABLED=true in .env)
//   /signin, /signup,
//   /feature-onboarding,
//   /useraccount-explore,
//   /signout,
//   /user-feed/sample     → depends on which button on the landing page was used:
//                             "Join Kleido AI"       → src-beta/App.jsx at /  (API calls)
//                             "Experience Kleido AI" → src/App.jsx            (demo, local storage)
//   everything else       → src/App.jsx                (demo)
import React, { Suspense, lazy } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { ProfileProvider } from "./context/ProfileContext";

// REACT_APP_* values are fixed at build time. When the flag is not "true",
// this condition is a constant `false` in the bundle and the beta code is left
// out of the build entirely, so demo users never download it.
// Keep the check inline here: moving it into a helper stops that from working.
const BetaApp =
  process.env.REACT_APP_JOIN_ENABLED === "true"
    ? lazy(() => import(/* webpackChunkName: "beta" */ "@beta/App"))
    : null;

const BETA_BASE = "/beta";
const isBetaPath = (pathname) =>
  pathname === BETA_BASE || pathname.startsWith(`${BETA_BASE}/`);

// Beta pages served WITHOUT the /beta prefix. The demo app has pages at the
// same URLs, so these go to the beta app (mounted at "/") only in beta mode.
const BETA_ROOT_PATHS = [
  "/signin",
  "/signup",
  "/feature-onboarding",
  "/useraccount-explore",
  "/signout",
  "/user-feed/sample",
];
const isBetaRootPath = (pathname) =>
  BETA_ROOT_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));

// Set by the landing page: "demo" (Experience Kleido AI) or "beta" (Join
// Kleido AI). Keep the key and values in sync with LandingPage.jsx.
const APP_MODE_KEY = "kleidoAppMode";
const readAppMode = () => {
  try {
    return localStorage.getItem(APP_MODE_KEY);
  } catch {
    return null;
  }
};

// Which basename the beta app gets for this URL; null → demo app.
const pathname = window.location.pathname;
let betaBasename = null;
if (isBetaPath(pathname)) {
  betaBasename = BETA_BASE;
  // Opening /beta directly counts as choosing the beta app.
  try {
    localStorage.setItem(APP_MODE_KEY, "beta");
  } catch {
    /* storage blocked: the beta app still mounts for /beta */
  }
} else if (isBetaRootPath(pathname) && readAppMode() !== "demo") {
  betaBasename = "/";
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <ProfileProvider>
    {BetaApp && betaBasename !== null ? (
      <Suspense fallback={null}>
        <BetaApp basename={betaBasename} />
      </Suspense>
    ) : (
      <App />
    )}
  </ProfileProvider>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
